import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, SITE_URL } from "@/lib/api";
import { absUrl, ESTABLISHMENT, DEFAULT_OG_IMAGE } from "@/lib/seo";
import { LANGS, langInfo, localizePath, stripLang, useLang } from "@/i18n";

const SiteContext = createContext({ settings: {}, content: {}, apartments: [], options: [], seo: [], loading: true });

export function SiteProvider({ children }) {
  const lang = useLang();
  const [data, setData] = useState({ settings: {}, content: {}, apartments: [], options: [], seo: [], loading: true });
  useEffect(() => {
    // Contenus du back-office dans la langue courante (les données précédentes restent affichées pendant le chargement)
    let cancelled = false;
    api.get("/public/bootstrap", { params: { lang } }).then((r) => { if (!cancelled) setData({ ...r.data, loading: false }); })
      .catch(() => { if (!cancelled) setData((d) => ({ ...d, loading: false })); });
    return () => { cancelled = true; };
  }, [lang]);
  useEffect(() => {
    if (data.settings?.favicon_url) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = data.settings.favicon_url;
    }
  }, [data.settings?.favicon_url]);
  return <SiteContext.Provider value={data}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}='${key}']`);
  if (!value) { el?.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
}

export function useSeo({ title, description, image, index = true, jsonLd } = {}) {
  const { seo, settings } = useSite();
  const { pathname: fullPath } = useLocation();
  const lang = useLang();
  const pathname = stripLang(fullPath);
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const page = seo.find((s) => s.path === pathname);
    const siteName = settings.site_name || ESTABLISHMENT;
    const t = title || page?.title || siteName;
    const d = description || page?.description || "";
    const img = absUrl(image || page?.og_image || DEFAULT_OG_IMAGE);
    const idx = page ? page.index !== false && index : index;
    const clean = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
    const urlFor = (l) => { const p = localizePath(clean, l); return `${SITE_URL}${p === "/" ? "/" : p}`; };
    const url = urlFor(lang);
    document.title = t;
    setMeta("name", "description", d);
    setMeta("name", "robots", idx ? "index,follow,max-image-preview:large" : "noindex,nofollow");
    setMeta("property", "og:site_name", siteName);
    setMeta("property", "og:title", t);
    setMeta("property", "og:description", d);
    setMeta("property", "og:image", img);
    setMeta("property", "og:image:alt", img ? t : "");
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:locale", langInfo(lang).ogLocale);
    // hreflang : même page dans les 4 langues (+ x-default = français)
    document.head.querySelectorAll("link[rel='alternate'][hreflang]").forEach((el) => el.remove());
    if (idx) {
      [...LANGS.map((l) => [l.htmlLang, urlFor(l.code)]), ["x-default", urlFor("fr")]].forEach(([hl, href]) => {
        const link = document.createElement("link"); link.rel = "alternate"; link.hreflang = hl; link.href = href; document.head.appendChild(link);
      });
    }
    setMeta("name", "twitter:card", img ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", t);
    setMeta("name", "twitter:description", d);
    setMeta("name", "twitter:image", img);
    setMeta("name", "twitter:image:alt", img ? t : "");
    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = url;
    document.getElementById("jsonld")?.remove();
    if (jsonLdKey) {
      // Les données dynamiques (à jour depuis le back-office) remplacent le JSON-LD statique d'index.html
      document.getElementById("jsonld-static")?.remove();
      const s = document.createElement("script"); s.type = "application/ld+json"; s.id = "jsonld";
      s.textContent = jsonLdKey; document.head.appendChild(s);
    }
  }, [title, description, image, index, jsonLdKey, pathname, lang, seo, settings.site_name]);
}
